import { usePostHog } from 'posthog-js/react';
import { useCallback, useMemo, useState } from 'react';

import {
    type ComposerPrefillSource,
    useUI,
} from '@apps/web/context/ui_provider';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { EVENTS } from '@apps/web/lib/events';

export const BULK_ASK_AVAILABILITY_BATCH_SIZE = 10;

// The composer message only carries names, not ids — two distinct selected
// caregivers sharing a display name would otherwise collapse into one
// repeated string with no way to tell them apart. Appends "(2)", "(3)", ...
// to later occurrences of a duplicate name; unique names are untouched.
function disambiguateNames(
    selected: ReadonlyMap<string, string>,
): ReadonlyMap<string, string> {
    const nameCounts = new Map<string, number>();
    for (const name of selected.values()) {
        nameCounts.set(name, (nameCounts.get(name) ?? 0) + 1);
    }
    const occurrenceSoFar = new Map<string, number>();
    const disambiguated = new Map<string, string>();
    for (const [caregiverId, name] of selected) {
        if ((nameCounts.get(name) ?? 0) <= 1) {
            disambiguated.set(caregiverId, name);
            continue;
        }
        const occurrence = (occurrenceSoFar.get(name) ?? 0) + 1;
        occurrenceSoFar.set(name, occurrence);
        disambiguated.set(caregiverId, `${name} (${occurrence})`);
    }
    return disambiguated;
}

// Selection is keyed by caregiver_id; the name travels alongside it (captured
// at select time) so a paginated list can lose a row from view without
// losing that caregiver's name for the composer message.
export function useBulkAskAvailability(source: ComposerPrefillSource) {
    const [selected, setSelected] = useState<ReadonlyMap<string, string>>(
        new Map(),
    );
    const [askedBatchCount, setAskedBatchCount] = useState(0);
    // Correlates every batch asked from one continuous selection into a
    // single "session" for analysis (adoption, scale, and completion/drop-off
    // funnels), without identifying which caregivers were involved.
    const [selectionId, setSelectionId] = useState<string | null>(null);
    const { openChatWithComposerText } = useUI();
    const organizationId = useOrganizationId();
    const posthog = usePostHog();

    const toggle = useCallback(
        (caregiverId: string, name: string) => {
            const willAdd = !selected.has(caregiverId);
            const nextSize = willAdd ? selected.size + 1 : selected.size - 1;
            if (selected.size === 0 && nextSize > 0) {
                setSelectionId(crypto.randomUUID());
            } else if (selected.size > 0 && nextSize === 0) {
                setSelectionId(null);
            }
            setSelected((prev) => {
                const next = new Map(prev);
                if (next.has(caregiverId)) {
                    next.delete(caregiverId);
                } else {
                    next.set(caregiverId, name);
                }
                return next;
            });
            setAskedBatchCount(0);
        },
        [selected],
    );

    const isSelected = useCallback(
        (caregiverId: string) => selected.has(caregiverId),
        [selected],
    );

    const batches = useMemo(() => {
        const names = [...disambiguateNames(selected).values()];
        const chunks: Array<Array<string>> = [];
        for (
            let start = 0;
            start < names.length;
            start += BULK_ASK_AVAILABILITY_BATCH_SIZE
        ) {
            chunks.push(
                names.slice(start, start + BULK_ASK_AVAILABILITY_BATCH_SIZE),
            );
        }
        return chunks;
    }, [selected]);

    const clear = useCallback(() => {
        if (selected.size > 0) {
            try {
                posthog?.capture(
                    EVENTS.BULK_ASK_AVAILABILITY_SELECTION_CLEARED,
                    {
                        organization_id: organizationId,
                        surface: source,
                        selection_id: selectionId,
                        selected_count: selected.size,
                        batches_asked: askedBatchCount,
                        batch_count: batches.length,
                    },
                );
            } catch {
                // Best-effort.
            }
        }
        setSelected(new Map());
        setSelectionId(null);
        setAskedBatchCount(0);
    }, [
        askedBatchCount,
        batches.length,
        organizationId,
        posthog,
        selected,
        selectionId,
        source,
    ]);

    // A selection change resets askedBatchCount to 0, but derive the clamp
    // here too rather than trust that reset alone: it keeps this value
    // correct under any future call order without an effect.
    const nextBatchIndex = Math.min(askedBatchCount, batches.length - 1);
    const allBatchesAsked =
        batches.length > 0 && askedBatchCount >= batches.length;

    const askNextBatch = useCallback(
        (buildMessage: (names: ReadonlyArray<string>) => string) => {
            const names = batches[nextBatchIndex];
            if (!names || names.length === 0) {
                return;
            }
            const batchNumber = nextBatchIndex + 1;
            const batchCountAtAsk = batches.length;
            // Capture once the composer confirms delivery (chat_view.tsx's
            // onDelivered), not at seed time — the composer can defer or
            // skip delivery (blocked input, wrong conversation target), so
            // "requested" should mean "the coordinator could actually see
            // it," matching the sibling learn_phoebe/calendar_selection events.
            openChatWithComposerText(buildMessage(names), source, () => {
                try {
                    posthog?.capture(EVENTS.BULK_ASK_AVAILABILITY_REQUESTED, {
                        organization_id: organizationId,
                        surface: source,
                        selection_id: selectionId,
                        batch_size: names.length,
                        batch_number: batchNumber,
                        batch_count: batchCountAtAsk,
                    });
                } catch {
                    // Best-effort.
                }
            });
            // Advance the stepper immediately regardless of delivery
            // timing — the UI must reflect "asked" right away.
            setAskedBatchCount((prev) => Math.min(prev + 1, batches.length));
        },
        [
            batches,
            nextBatchIndex,
            openChatWithComposerText,
            organizationId,
            posthog,
            selectionId,
            source,
        ],
    );

    return {
        selectedCount: selected.size,
        isSelected,
        toggle,
        clear,
        batchCount: batches.length,
        nextBatchNumber: nextBatchIndex + 1,
        allBatchesAsked,
        askNextBatch,
    };
}
